import { type MembershipPricingData } from '@/features/cms/components/CMS/types'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'

import { Badge } from '@shipt/design-system-badge'
import { Button } from '@shipt/design-system-buttons'
import { Column } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  isBestValue: boolean
  callout: string
  name: string
  isDiscounted: boolean
  originalPrice: number
  price: number
  buttonCta: string
  onButtonClick: () => void
  interval: string
  description: string
  href?: string
  buttonAriaLabel?: string
  trackViewed?: () => void
  isCarousel?: boolean
  themeMode?: MembershipPricingData['data']['theme']
  priceText?: string
  pricePeriodText?: string
  originalPriceText?: string
  hidePrice?: boolean
  hideNameSuffix?: boolean
  creditText?: string
}

export const PlanCardV2 = ({
  isBestValue,
  callout,
  name,
  isDiscounted,
  originalPrice,
  price,
  interval,
  description,
  buttonCta,
  onButtonClick,
  href,
  buttonAriaLabel,
  trackViewed,
  isCarousel = false,
  themeMode,
  priceText,
  pricePeriodText,
  originalPriceText,
  hidePrice = false,
  hideNameSuffix = false,
  creditText,
}: Props) => {
  const isDarkThemeCard = Boolean(themeMode === 'dark') && !isBestValue
  const displayPriceText = priceText || `$${price}`
  const displayOriginalPriceText = originalPriceText || `$${originalPrice}`
  let displayPricePeriodText = pricePeriodText || interval
  if (isDiscounted) {
    displayPricePeriodText = pricePeriodText ?? `first ${interval}`
  }

  return (
    <CardScrollIntoView
      $isBestValue={isBestValue}
      key={name}
      onInView={() => trackViewed?.()}
      data-is-dark-theme-card={isDarkThemeCard}
      data-is-carousel={isCarousel}
    >
      {!!isBestValue && !!callout && (
        <BestValueCallout size="sm" variant="promotion" concept="feedback" hasEmphasis>
          {callout}
        </BestValueCallout>
      )}
      <CardTextContainer spacing="lg" justify="space-between">
        <div>
          <Body
            size="xl"
            variant={isDarkThemeCard ? 'primary' : 'secondary'}
            surface={isDarkThemeCard ? 'inverse' : 'default'}
          >
            {hideNameSuffix ? name : `${name} plan`}
          </Body>
          {!hidePrice &&
            (isDiscounted ? (
              <Headline size="xl" as="h3" surface={isDarkThemeCard ? 'inverse' : 'default'}>
                <OldPrice isDarkThemeCard={isDarkThemeCard}>{displayOriginalPriceText}</OldPrice>{' '}
                <PromoPrice>{displayPriceText}</PromoPrice>/{displayPricePeriodText}
              </Headline>
            ) : (
              <Headline size="xl" as="h3" surface={isDarkThemeCard ? 'inverse' : 'default'}>
                {displayPriceText}/{displayPricePeriodText}
              </Headline>
            ))}

          {!!creditText && (
            <CreditText concept="feedback" variant="secondary" size="md">
              {creditText}
            </CreditText>
          )}

          <Description
            variant={isDarkThemeCard ? 'primary' : 'secondary'}
            surface={isDarkThemeCard ? 'inverse' : 'default'}
            size="md"
          >
            {description}
          </Description>
        </div>
        {href ? (
          <ButtonLink
            prefetch={false}
            onClick={onButtonClick}
            size="md"
            aria-describedby="bold_text"
            aria-label={buttonAriaLabel}
            href={href}
            variant={isBestValue || !isDarkThemeCard ? 'primary' : 'secondary'}
            surface={isDarkThemeCard ? 'inverse' : 'default'}
          >
            {buttonCta}
          </ButtonLink>
        ) : (
          <Button
            concept="action"
            size="md"
            variant={isBestValue || !isDarkThemeCard ? 'primary' : 'secondary'}
            surface={isDarkThemeCard ? 'inverse' : 'default'}
            onClick={onButtonClick}
            aria-label={`${buttonCta} ${name}`}
          >
            {buttonCta}
          </Button>
        )}
      </CardTextContainer>
    </CardScrollIntoView>
  )
}

const CardScrollIntoView = styled(ScrollIntoView)<{
  'data-is-dark-theme-card'?: boolean
  'data-is-carousel'?: boolean
  $isBestValue?: boolean
}>`
  background-color: ${theme.color.background.base.neutral.default};
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  position: relative;
  height: auto;

  &[data-is-dark-theme-card='true'] {
    border: 2px solid ${theme.color.border.base.neutral.inverse};
    background-color: ${theme.color.background.base.brand.primary_heavy};
  }

  @media ${screenSizes.max_mobile} {
    &[data-is-carousel='true'] {
      margin: 1rem 0 1.5rem;
      width: 17.5rem;
    }
  }

  @media ${screenSizes.tablet} {
    width: calc(50% - 0.5rem);
  }
`

const CardTextContainer = styled(Column)`
  height: 100%;
`

const BestValueCallout = styled(Badge)`
  top: -0.75rem;
  left: 1rem;
  position: absolute;
`

const OldPrice = styled.span<{ isDarkThemeCard?: boolean }>`
  text-decoration: line-through;
  color: ${({ isDarkThemeCard }) =>
    isDarkThemeCard
      ? theme.color.content.base.neutral.primary_inverse
      : theme.color.content.base.neutral.secondary};
  font-weight: 300;
`

const PromoPrice = styled.span`
  color: ${theme.color.content.feedback.promotion.primary};
`

const CreditText = styled(Body)`
  margin: 0.5rem 0;
`

const Description = styled(Body)`
  white-space: pre-line;
`
